'use client'

import React, { useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import { Offer, ProductData } from '@/services/dataTypes'
import { getFinalDiscountTag } from '@/constants/hooks'
import { Eye } from 'lucide-react'

const loadDetails = () => import('./OfferDetailsModal')
const OfferDetailsModal = dynamic(loadDetails, { ssr: false })
const OfferModal = dynamic(() => import('./OfferModal'), { ssr: false })
const ProductDetailsModal = dynamic(() => import('./ProductDetailsModal'), { ssr: false })
const ProductCommentModal = dynamic(() => import('./ProductCommentModal'), { ssr: false })

interface Props {
    offer: Offer | ProductData
    merchantHref: string
    imageSrc: string
    merchantImg?: string | null
    domain: string
    type: 'anchor' | 'button'
    buttonClass?: string
    modalType?: 'simple' | 'details' | 'offer' | 'product' | 'comment'
    companyId?: string
    merchantName?: string
}

const OfferDetailsToggle = ({
    offer,
    merchantHref,
    domain,
    type,
    buttonClass,
    imageSrc,
    merchantImg,
    modalType = 'comment',
    companyId = '',
    merchantName = ''
}: Props) => {
    const [showModal, setShowModal] = useState(false)

    useEffect(() => {
        if (modalType !== 'details') return
        const id = setTimeout(() => { void loadDetails() }, 800)
        return () => clearTimeout(id)
    }, [modalType])

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setShowModal(true)
    }

    const originalPrice = offer?.original_price ? parseFloat(offer.original_price) : 0
    const salePrice = offer?.sale_price ? parseFloat(offer.sale_price) : 0
    const discountPercent = originalPrice > 0 && salePrice > 0
        ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const finalDiscountTag = getFinalDiscountTag(offer?.offer_title || offer?.offer_detail, discountPercent)
    const computedMerchantName = merchantName || (offer as Offer)?.merchant?.merchant_name || 'Brand'
    const computedMerchantLogo = merchantImg || (offer as Offer)?.merchant?.merchant_logo || ''
    const computedProductImg =
        imageSrc ||
        (offer as any)?.product_image ||
        (offer as any)?.offer?.product_image ||
        (offer as any)?.image ||
        ''

    return (
        <>
            <button
                onClick={handleClick}
                onMouseEnter={() => { void loadDetails() }}
                onTouchStart={() => { void loadDetails() }}
                role={type === 'anchor' ? 'presentation' : undefined}
                className={`inline-flex items-center gap-1 text-xs text-[#00a87a] cursor-pointer hover:underline font-bold transition-colors ${buttonClass ?? ''}`}
            >
                <Eye size={12} /> View Details
            </button>

            {showModal && modalType === 'details' && (
                <OfferDetailsModal
                    data={offer}
                    onClose={() => setShowModal(false)}
                    domain={domain}
                    finalDiscountTag={finalDiscountTag}
                    merchantImg={computedMerchantLogo}
                    productImg={computedProductImg}
                    companyId={companyId}
                />
            )}

            {showModal && (modalType === 'comment' || modalType === 'simple') && (
                <ProductCommentModal
                    product={offer as Offer}
                    merchantName={computedMerchantName}
                    merchantLogo={computedMerchantLogo}
                    domain={domain}
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    productImg={computedProductImg}
                    companyId={companyId}
                />
            )}

            {showModal && modalType === 'offer' && (
                <OfferModal
                    data={offer as Offer}
                    companyId={companyId}
                    onClose={() => setShowModal(false)}
                    domain={domain}
                    productImg={computedProductImg}
                    merchantImg={computedMerchantLogo}
                />
            )}

            {showModal && modalType === 'product' && (
                <ProductDetailsModal
                    product={offer as Offer}
                    merchantName={computedMerchantName}
                    merchantLogo={computedMerchantLogo}
                    domain={domain}
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    companyId={companyId}
                    productImg={computedProductImg}
                />
            )}
        </>
    )
}

export default OfferDetailsToggle
